import React from 'react';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';

export interface SkeletonLineConfig {
    width: string | number;
    height?: string | number;
}

// Props for skeleton types
interface HBLoadingSkeletonProps {
    /** Skeleton layout preset: 'text' renders lines only, 'image_title_and_text' adds a circular avatar and title above the lines. */
    type?: 'text' | 'image_title_and_text';
    /** Number of skeleton text lines to render with randomized widths (80-100%). Ignored when `lines` is provided. */
    numOfLines?: number;
    /** Custom line configurations. Overrides numOfLines when provided. */
    lines?: SkeletonLineConfig[];
    /** Inline styles forwarded to each skeleton line element. */
    style?: React.CSSProperties;
}

export type { HBLoadingSkeletonProps };

const TextSkeletonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

function TextSkeleton({
    numOfLines = 3,
    lines,
    style
}: {
    numOfLines?: number;
    lines?: SkeletonLineConfig[];
    style?: React.CSSProperties;
}) {
    const skeletonLines =
        lines && lines.length > 0
            ? lines.map((config, i) => (
                  <Skeleton
                      key={i}
                      width={config.width}
                      height={config.height}
                      style={style}
                  />
              ))
            : Array.from({ length: numOfLines }, (_, i) => (
                  <Skeleton
                      key={i}
                      width={`${
                          Math.floor(Math.random() * (100 - 80 + 1)) + 80
                      }%`}
                      style={style}
                  />
              ));

    return <TextSkeletonWrapper>{skeletonLines}</TextSkeletonWrapper>;
}

const ImageAndTitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ImageTitleTextSkeletonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

function ImageTitleTextSkeleton({
    numOfLines,
    lines,
    style
}: {
    numOfLines?: number;
    lines?: SkeletonLineConfig[];
    style?: React.CSSProperties;
}) {
    return (
        <ImageTitleTextSkeletonWrapper>
            <ImageAndTitleWrapper>
                <Skeleton circle={true} height={50} width={50} />
                <Skeleton width={150} />
            </ImageAndTitleWrapper>
            <TextSkeleton numOfLines={numOfLines} lines={lines} style={style} />
        </ImageTitleTextSkeletonWrapper>
    );
}

const HBLoadingSkeleton = (props: HBLoadingSkeletonProps) => {
    const { type, numOfLines, lines, style } = props;

    switch (type) {
        case 'image_title_and_text':
            return (
                <ImageTitleTextSkeleton
                    numOfLines={numOfLines}
                    lines={lines}
                    style={style}
                />
            );
        default:
            return (
                <TextSkeleton
                    numOfLines={numOfLines}
                    lines={lines}
                    style={style}
                />
            );
    }
};

export default HBLoadingSkeleton;
